/* eslint-disable no-unused-vars, max-len */
import { curry, PlatformEvent } from 'lib/util'
import sequenceComponentFindProxy from 'lib/sequence/component/children/find'
import { hidden } from 'lib/sequence/component/state/hidden'
import { disabled } from 'lib/sequence/component/state/disabled'

/**
 * A static list of field types that are allowed to be taken into consideration
 * for focusing.
 */
const allowedFieldTypes: string[] = [
  'text',
  'autocomplete',
  // 'date', // TODO: See GAIA-1414
  // 'time', // TODO: See GAIA-1414
]

/**
 * Predicate function that checks if `field` can be focused.
 *
 * @param field the field to check
 */
const canBeFocused = (field: Gaia.Component.Spec): boolean => !!allowedFieldTypes
  .find(type => field?.[G.CONFIGURATION]?._name?.includes?.(type))
  && !hidden(field)
  && !disabled(field)

/**
 * Recursive function that walks down the component tree and finds all fields for which
 * {@link canBeFocused} returned `true`. Will collect all fields in an array and
 * returns it.
 *
 * @param module    app module
 * @param component action component
 * @param event     the event that triggered this handler
 */
export const findFocusableFields = (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec,
  event: PlatformEvent<{
    field: Gaia.Component.Spec,
    result?: Gaia.Component.Spec[],
  }>,
): Gaia.Component.Spec[] => {
  const { field, result = [] } = event?.detail || event || {}

  canBeFocused(field)
    ? result.push(field)
    : (field?.[G.CHILDREN] || []).forEach((child: Gaia.Component.Spec) => {
      findFocusableFields(module, component, new PlatformEvent(null, { field: child, result }))
    })

  return result
}

/**
 * Focus Next Event Handler
 *
 * Will determine the next field that is below `root` (or the action component) in the component
 * tree that can be focused and will do so. If no field could be found it'll blur the current
 * field, closing the keyboard.
 *
 * @param root      name of the up-most component to look for fields.
 *                  If not defined, the action component will be used.
 * @param module    app module
 * @param component action component
 * @param event     the event that triggered this handler
 */
const focusNext = (
  root: string,
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec,
  event: PlatformEvent,
): void => {
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const { [root]: rootComponent } = sequenceComponentFindProxy(actionComponent)
  const parent = root ? rootComponent : actionComponent

  const fields = findFocusableFields(module, component, new PlatformEvent(null, { field: parent }))
  const currentFieldIndex = fields.findIndex(field => field._name === component._name)
  const nextFieldIndex = currentFieldIndex === -1 || currentFieldIndex + 1 >= fields.length
    ? null
    : currentFieldIndex + 1

  const nextField = nextFieldIndex !== null && fields[nextFieldIndex]
  const currentField = fields?.[currentFieldIndex]

  if (nextField) {
    nextField[G.STATE].focus = {}
    module[G.ADAPTER][G.UI].update(module)
    // We don't autoFocus to trigger the next time the UI is rendered
    nextField[G.STATE].focus = false
  } else if (currentField) {
    currentField[G.STATE].blur = {}
    module[G.ADAPTER][G.UI].update(module)
    currentField[G.STATE].blur = false
  }
}

export default curry(focusNext)
